import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';

import { addMinutes, formatISO, isValid, parseISO } from 'date-fns';
import { combineLatest, firstValueFrom, map, Observable } from 'rxjs';
import {
  type FirestoreTimer,
  type FirestoreProject,
  FirestoreWork,
  getInterval,
  IsoDate,
  type TimeEntry,
  type Project,
  Work,
  WorkEntity2,
  WorkFilter,
  workMinutesAfterTimeEntryDelete,
  workMinutesAfterTimeEntryUpdate,
} from '@jessie/commons';
import { LastEditorService } from './last-editor.service';
import { FIRESTORE_SERVICE, ORGA_FIRESTORE_SERVICE } from './firestore.service';
import {
  collection,
  doc,
  Firestore,
  limit,
  orderBy,
  QueryConstraint,
  runTransaction,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import { SessionStateService } from './session-state.service';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class WorkService {
  private injector = inject(EnvironmentInjector);
  private rootFirestoreService = inject(FIRESTORE_SERVICE);
  private orgaFirestoreService = inject(ORGA_FIRESTORE_SERVICE);

  private sessionState = inject(SessionStateService);
  private lastEditor = inject(LastEditorService);

  firestore = inject(Firestore);
  private runFirestoreTransaction = runTransaction;

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  getWork(projectId: string, positionId: string, workId: string) {
    return this.orgaFirestoreService.getDoc<Work>(`project/${projectId}/work/${workId}`);
  }

  countWork(projectId: string, positionId: string) {
    return this.orgaFirestoreService.count(
      `project/${projectId}/work`,
      this.inCtx(() => where('position.id', '==', positionId))
    );
  }

  getWorkByDocRef(workDocRef: string): Observable<FirestoreWork> {
    return this.rootFirestoreService.getDoc<Work>(workDocRef);
  }

  getAllWorkBetweenDate(from: Date, to: Date): Observable<FirestoreWork[]> {
    return this.sessionState.getOrgaId().pipe(
      switchMap((orgaId) =>
        this.orgaFirestoreService.getDocsByCollectionGroup<Work>(
          'work',
          this.inCtx(() => where('orgaId', '==', orgaId)),
          this.inCtx(() => where('date', '>=', formatISO(from, { representation: 'date' }))),
          this.inCtx(() => where('date', '<=', formatISO(to, { representation: 'date' })))
        )
      )
    );
  }

  getAllWorkForPosition(projectId: string, positionId: string): Observable<FirestoreWork[]> {
    return this.orgaFirestoreService.getDocs<Work>(
      `project/${projectId}/work`,
      this.inCtx(() => where('positionId', '==', positionId))
    );
  }

  getAllWorkForPositionBetweenDate(
    projectId: string,
    positionId: string,
    from: Date,
    to: Date
  ): Observable<FirestoreWork[]> {
    return this.orgaFirestoreService.getDocs<Work>(
      `project/${projectId}/work`,
      this.inCtx(() => where('positionId', '==', positionId)),
      this.inCtx(() => where('date', '>=', formatISO(from, { representation: 'date' }))),
      this.inCtx(() => where('date', '<=', formatISO(to, { representation: 'date' })))
    );
  }

  getAllBillableWorkForPositionBetweenDate(
    projectId: string,
    positionId: string,
    from: Date,
    to: Date
  ): Observable<FirestoreWork[]> {
    return this.orgaFirestoreService.getDocs<Work>(
      `project/${projectId}/work`,
      this.inCtx(() => where('positionId', '==', positionId)),
      this.inCtx(() => where('date', '>=', formatISO(from, { representation: 'date' }))),
      this.inCtx(() => where('date', '<=', formatISO(to, { representation: 'date' }))),
      this.inCtx(() => where('billed', '==', false))
    );
  }

  getAllWorkForUser(orgaUserId: string, from: IsoDate, to: IsoDate): Observable<FirestoreWork[]> {
    return this.sessionState.getOrgaId().pipe(
      switchMap((orgaId) =>
        this.orgaFirestoreService.getDocsByCollectionGroup<Work>(
          'work',
          this.inCtx(() => where('orgaUser.id', '==', orgaUserId)),
          this.inCtx(() => where('orgaId', '==', orgaId)),
          this.inCtx(() => where('date', '>=', from)),
          this.inCtx(() => where('date', '<=', to))
        )
      )
    );
  }

  getAllWorkForProject(underProjectId: string): Observable<FirestoreWork[]> {
    return this.sessionState.getOrgaId().pipe(
      switchMap((orgaId) =>
        this.orgaFirestoreService.getDocsByCollectionGroup<Work>(
          'work',
          this.inCtx(() => where('projectId', '==', underProjectId)),
          this.inCtx(() => where('orgaId', '==', orgaId))
        )
      )
    );
  }

  getWorkByFilter(filter: WorkFilter): Observable<FirestoreWork[]> {
    return this.sessionState.getOrgaId().pipe(
      map((orgaId) => {
        const filterQuery: QueryConstraint[] = [this.inCtx(() => where('orgaId', '==', orgaId))];
        if (filter.project) {
          filterQuery.push(this.inCtx(() => where('project.id', '==', filter.project!.id)));
        }
        if (filter.position) {
          filterQuery.push(this.inCtx(() => where('position.id', '==', filter.position!.id)));
        }
        if (filter.user) {
          filterQuery.push(this.inCtx(() => where('orgaUser.id', '==', filter.user!.id)));
        }
        if (filter.date.type !== 'notSet') {
          const interval = getInterval(filter.date);
          filterQuery.push(
            this.inCtx(() =>
              where('date', '>=', formatISO(interval.start, { representation: 'date' }))
            ),
            this.inCtx(() =>
              where('date', '<=', formatISO(interval.end, { representation: 'date' }))
            )
          );
        }
        filterQuery.push(this.inCtx(() => orderBy('date', 'asc')));
        return filterQuery;
      }),
      switchMap((filterQuery) =>
        this.orgaFirestoreService.getDocsByCollectionGroup<Work>('work', ...filterQuery)
      )
    );
  }

  getAllWorkForProjectBetween(
    underProjectId: string,
    from: Date,
    to: Date
  ): Observable<FirestoreWork[]> {
    return this.orgaFirestoreService.getDocs<Work>(
      `project/${underProjectId}/work`,
      this.inCtx(() => where('date', '>=', formatISO(from, { representation: 'date' }))),
      this.inCtx(() => where('date', '<=', formatISO(to, { representation: 'date' })))
    );
  }

  getAllBillableWorkForProjectBetweenDate2(
    underProjectId: string,
    from: IsoDate,
    to: IsoDate
  ): Observable<FirestoreWork[]> {
    return this.orgaFirestoreService.getDocs<Work>(
      `project/${underProjectId}/work`,
      this.inCtx(() => where('date', '>=', from)),
      this.inCtx(() => where('date', '<=', to)),
      this.inCtx(() => where('position.billable', '==', true)),
      this.inCtx(() => where('position.type', '==', 'effort')),
      this.inCtx(() => where('invoice', '==', null))
    );
  }

  async createWork(work: WorkEntity2) {
    return this.orgaFirestoreService.createDoc(
      `project/${work.project.id}/work`,
      await this.lastEditor.hydrate(work)
    );
  }

  getRecentWorkForUser(orgaUserId: string, limitCount = 5): Observable<FirestoreWork[]> {
    return this.sessionState.getOrgaId().pipe(
      switchMap((orgaId) =>
        this.orgaFirestoreService.getDocsByCollectionGroup<Work>(
          'work',
          this.inCtx(() => where('orgaUser.id', '==', orgaUserId)),
          this.inCtx(() => where('orgaId', '==', orgaId)),
          this.inCtx(() => orderBy('date', 'desc')),
          this.inCtx(() => limit(limitCount))
        )
      )
    );
  }

  getWorkSuggestions(
    orgaUserId: string,
    recentCount = 20,
    topN = 5,
    activeProjects$?: Observable<FirestoreProject[]>
  ): Observable<FirestoreWork[]> {
    return combineLatest([
      this.getRecentWorkForUser(orgaUserId, recentCount),
      activeProjects$ ??
      this.orgaFirestoreService.getDocs<Project>(
        'project',
        this.inCtx(() => where('isActive', '==', true))
      ),
    ]).pipe(
      map(([works, activeProjects]) => {
        const activePositionsByProject = new Map(
          activeProjects.map((project) => [
            project.id,
            new Set(
              Object.entries(project.data.positions ?? {})
                .filter(([, position]) => position.isActive === undefined || position.isActive)
                .map(([positionId]) => positionId)
            ),
          ])
        );
        const freqMap = new Map<
          string,
          { work: FirestoreWork; count: number; firstSeen: number }
        >();
        works.forEach((work, index) => {
          const projectId = work.data.project?.id;
          const positionId = work.data.position?.id;
          if (!projectId || !positionId) {
            return;
          }

          const activePositionIds = activePositionsByProject.get(projectId);
          if (!activePositionIds?.has(positionId)) {
            return;
          }

          const key = JSON.stringify([projectId, positionId]);
          const entry = freqMap.get(key);
          if (entry) {
            entry.count++;
          } else {
            freqMap.set(key, { work, count: 1, firstSeen: index });
          }
        });

        return Array.from(freqMap.values())
          .sort((a, b) => b.count - a.count || a.firstSeen - b.firstSeen)
          .slice(0, topN)
          .map((entry) => entry.work);
      })
    );
  }

  async createWorks(works: WorkEntity2[]) {
    const orgaId = await firstValueFrom(this.sessionState.getOrgaId());
    return this.inCtx(async () => {
      const batch = writeBatch(this.firestore);
      //add orgaId
      const updatedWorks = works.map((work) => ({ ...work, orgaId }));
      await Promise.all(
        updatedWorks.map(async (work) =>
          batch.set(
            doc(collection(this.firestore, `orga/${orgaId}/project/${work.project.id}/work`)),
            await this.lastEditor.hydrate(work)
          )
        )
      );
      return batch.commit();
    });
  }

  async createWorkFromTimer(timer: FirestoreTimer, work: WorkEntity2): Promise<void> {
    if (!timer.docRef) {
      throw new Error('Cannot convert timer without docRef');
    }

    const orgaId = await firstValueFrom(this.sessionState.getOrgaId());
    return this.inCtx(async () => {
      const batch = writeBatch(this.firestore);
      const workDocRef = doc(
        collection(this.firestore, `orga/${orgaId}/project/${work.project.id}/work`)
      );

      batch.set(workDocRef, await this.lastEditor.hydrate({ ...work, orgaId }));
      batch.delete(doc(this.firestore, timer.docRef.path));
      await batch.commit();
    });
  }

  async updateWork(oldWork: FirestoreWork, update: Partial<WorkEntity2>): Promise<void> {
    if (!oldWork.docRef) {
      throw new Error('Cannot update work without docRef');
    }
    if (oldWork.data.project.id !== update.project?.id) {
      await this.deleteWork(oldWork);
      await this.createWorks([{ ...oldWork.data, ...update }]);
      return;
    }
    return this.updateWorkInternal(update, oldWork.docRef.path);
  }

  async updateWorkInternal(work: Partial<Work>, docRefPath: string): Promise<void> {
    return this.rootFirestoreService.updateDoc<Work>(
      docRefPath,
      await this.lastEditor.hydrate(work)
    );
  }

  async updateWorkSession(
    work: FirestoreWork,
    sessionEntryId: string,
    update: Partial<WorkEntity2>,
    nextEntry: TimeEntry
  ): Promise<void> {
    if (!work.docRef) {
      throw new Error('Cannot update work session without docRef');
    }

    const applySessionUpdate = (allowProjectMoveDelete: boolean) =>
      this.inCtx(() =>
        this.runFirestoreTransaction(
          this.firestore,
          async (transaction): Promise<'updated' | 'move-required'> => {
            const workRef = doc(this.firestore, work.docRef.path);
            const workDoc = await transaction.get(workRef);
            if (!workDoc.exists()) {
              throw new Error(`Work with id ${work.id} does not exist`);
            }

            const latestWork = workDoc.data() as WorkEntity2;
            if (latestWork.invoice) {
              throw new Error(`Work with id ${work.id} is invoiced`);
            }

            const latestTimeEntries = latestWork.timeEntries;
            const latestSessionEntry = latestTimeEntries?.[sessionEntryId];
            if (!latestSessionEntry) {
              throw new Error(`Time entry with id ${sessionEntryId} does not exist`);
            }

            const expectedSessionEntry = work.data.timeEntries?.[sessionEntryId];
            if (
              !expectedSessionEntry ||
              expectedSessionEntry.startTime !== latestSessionEntry.startTime ||
              expectedSessionEntry.totalMinutes !== latestSessionEntry.totalMinutes
            ) {
              throw new Error(`Time entry with id ${sessionEntryId} has changed`);
            }

            const originalStart = parseISO(latestSessionEntry.startTime);
            const originalEnd = addMinutes(originalStart, latestSessionEntry.totalMinutes);
            const nextStart = parseISO(nextEntry.startTime);
            if (
              !isValid(originalStart) ||
              !isValid(originalEnd) ||
              !isValid(nextStart) ||
              !Number.isFinite(latestSessionEntry.totalMinutes) ||
              latestSessionEntry.totalMinutes < 1 ||
              !Number.isFinite(nextEntry.totalMinutes) ||
              nextEntry.totalMinutes < 1
            ) {
              throw new Error(`Time entry with id ${sessionEntryId} is invalid`);
            }

            const originalSessionDate = formatISO(originalStart, { representation: 'date' });
            const nextEnd = addMinutes(nextStart, nextEntry.totalMinutes);
            if (
              formatISO(originalEnd, { representation: 'date' }) !== originalSessionDate ||
              formatISO(nextStart, { representation: 'date' }) !== originalSessionDate ||
              formatISO(nextEnd, { representation: 'date' }) !== originalSessionDate
            ) {
              throw new Error(`Time entry with id ${sessionEntryId} must remain on the same day`);
            }

            const nextTimeEntries: Record<string, TimeEntry> = {
              ...latestTimeEntries,
              [sessionEntryId]: {
                startTime: nextEntry.startTime,
                totalMinutes: nextEntry.totalMinutes,
              },
            };
            const nextWork = {
              ...latestWork,
              ...update,
              timeEntries: nextTimeEntries,
              workMinutes: workMinutesAfterTimeEntryUpdate(
                latestWork.workMinutes,
                latestSessionEntry,
                nextEntry.totalMinutes
              ),
            };
            const nextProjectId = update.project?.id ?? latestWork.project.id;

            if (nextProjectId !== latestWork.project.id) {
              if (!allowProjectMoveDelete) {
                return 'move-required';
              }

              transaction.set(
                doc(
                  collection(
                    this.firestore,
                    `orga/${latestWork.orgaId}/project/${nextProjectId}/work`
                  )
                ),
                await this.lastEditor.hydrate(nextWork)
              );
              transaction.delete(workRef);
              return 'updated';
            }

            transaction.update(
              workRef,
              await this.lastEditor.hydrate({
                ...update,
                timeEntries: nextTimeEntries,
                workMinutes: nextWork.workMinutes,
              })
            );
            return 'updated';
          }
        )
      );

    const result = await applySessionUpdate(false);
    if (result === 'move-required') {
      await this.primeWorkDelete(work.docRef.path);
      await applySessionUpdate(true);
    }
  }

  async deleteWorkSession(
    work: FirestoreWork,
    sessionEntryId: string
  ): Promise<'deleted' | 'updated'> {
    if (!work.docRef) {
      throw new Error('Cannot delete work session without docRef');
    }

    const applySessionDelete = (allowWholeWorkDelete: boolean) =>
      this.inCtx(() =>
        runTransaction(
          this.firestore,
          async (transaction): Promise<'deleted' | 'updated' | 'delete-required'> => {
            const workRef = doc(this.firestore, work.docRef.path);
            const workDoc = await transaction.get(workRef);
            if (!workDoc.exists()) {
              throw new Error(`Work with id ${work.id} does not exist`);
            }

            const latestWork = workDoc.data() as WorkEntity2;
            const latestTimeEntries = latestWork.timeEntries;
            if (!latestTimeEntries?.[sessionEntryId]) {
              return 'updated';
            }

            const { [sessionEntryId]: _deleted, ...remainingTimeEntries } = latestTimeEntries;
            if (Object.keys(remainingTimeEntries).length === 0) {
              if (!allowWholeWorkDelete) {
                return 'delete-required';
              }

              transaction.delete(workRef);
              return 'deleted';
            }

            transaction.update(
              workRef,
              await this.lastEditor.hydrate({
                timeEntries: remainingTimeEntries,
                workMinutes: workMinutesAfterTimeEntryDelete(
                  latestWork.workMinutes,
                  latestTimeEntries[sessionEntryId]
                ),
              })
            );
            return 'updated';
          }
        )
      );

    const result = await applySessionDelete(false);
    if (result !== 'delete-required') {
      return result;
    }

    await this.primeWorkDelete(work.docRef.path);
    const deleteResult = await applySessionDelete(true);
    return deleteResult === 'delete-required' ? 'updated' : deleteResult;
  }

  private async primeWorkDelete(workDocRefPath: string): Promise<void> {
    await this.updateWorkInternal({}, workDocRefPath);
  }

  async deleteWork(work: FirestoreWork): Promise<unknown> {
    // do a update to insert lastEditor, else a delete would fail
    await this.primeWorkDelete(work.docRef.path);
    return this.rootFirestoreService.deleteDoc(work.docRef.path);
  }

  async bulkUpdateInvoiceState(
    works: Work[],
    invoiceId: string,
    invoiceNumber: string,
    billed: boolean
  ) {
    return this.inCtx(() =>
      runTransaction(this.firestore, async (transaction) => {
        const workDocs = await Promise.all(
          works.map(async (work) => {
            const workDoc = await transaction.get(
              doc(this.firestore, `orga/${work.orgaId}/project/${work.project.id}/work/${work.id}`)
            );
            if (!workDoc.exists()) {
              throw `Work with id ${work.id} does not exist`;
            }
            const workEntity: WorkEntity2 = workDoc.data() as WorkEntity2;

            if (billed && workEntity.invoice) {
              throw `Work with id ${work.id} already billed`;
            }
            if (!billed && workEntity.invoice && workEntity.invoice.id !== invoiceId) {
              throw `Work with id ${work.id} is is not billed with this invoiceId (${workEntity.invoice.id} vs ${invoiceId}`;
            }
            return workDoc;
          })
        );

        await Promise.all(
          workDocs.map(async (workDoc) => {
            transaction.update(
              workDoc.ref,
              await this.lastEditor.hydrate({
                invoice: billed ? { id: invoiceId, invoiceNumber: invoiceNumber } : null,
              })
            );
          })
        );
      })
    );
  }
}
//
// export function createBaseWork(
//   work: {
//     description: string;
//     workMinutes: number;
//   },
//   user: {
//     userId: string;
//     userDisplayName: string;
//   },
//   orgaId: string,
//   project: FirestoreProject,
//   position: Position2
// ): BaseWork {
//   return {
//     orgaId,
//     userId: user.userId,
//     userDisplayName: user.userDisplayName,
//
//     description: work.description,
//     workMinutes: work.workMinutes,
//
//     positionId: position.id,
//     positionDisplayName: position.name,
//     positionBillable: position.billable,
//     positionType: position.type,
//
//     projectId: project.id,
//     projectDisplayName: project.data.name,
//     customerId: project.data.customerId,
//     customerName: project.data.customerName,
//
//     billed: false,
//   };
// }
